/**
 * Regulatory Compliance Repository
 * 
 * Frontend repository for tenant-configurable regulatory thresholds.
 * Implements FERP repository pattern for all data access.
 * 
 * FERP Compliance Requirements:
 * - Repository pattern for all data access
 * - Multi-tenant data isolation
 * - Immutable audit trails
 * - Life-critical safety validation
 */

import { BaseRepository } from './BaseRepository';

export interface ThresholdRecord {
    tenant: string;
    framework_id: string;
    threshold_type: string;
    value: number;
    unit: string;
    lastUpdated?: string;
    updatedBy?: string;
}

export interface ThresholdQuery {
    framework_id: string;
    threshold_type: string;
}

export interface EmergencyOverrideContext {
    justification: string;
    approvedBy: string;
    expectedDurationHours: number;
    requestedAt: string;
    expiresAt: string;
}

/**
 * Repository for regulatory compliance threshold management
 * Provides tenant-configurable safety thresholds replacing hardcoded values
 */
export abstract class RegulatoryComplianceRepository extends BaseRepository<ThresholdRecord> {
    /**
     * Get single threshold by tenant and query parameters
     */
    abstract getThreshold(
        tenantId: string,
        query: ThresholdQuery
    ): Promise<ThresholdRecord | null>;

    /**
     * Get multiple thresholds in a single batch request
     */
    abstract getThresholdsBatch(
        tenantId: string,
        queries: ThresholdQuery[]
    ): Promise<ThresholdRecord[]>;

    /**
     * Update threshold with audit trail
     */
    abstract updateThreshold(
        tenantId: string,
        record: ThresholdRecord
    ): Promise<ThresholdRecord>;

    /**
     * Emergency override threshold with enhanced audit logging
     * FERP Requirement: Emergency controls with immutable audit trails
     */
    abstract emergencyOverrideThreshold(
        tenantId: string,
        record: ThresholdRecord,
        justification: string,
        expectedDurationHours: number,
        approvedBy: string
    ): Promise<ThresholdRecord>;
}

/**
 * GraphQL implementation of RegulatoryComplianceRepository
 * Uses FERP's GraphQL gateway with proper tenant isolation
 */
export class GraphQLRegulatoryComplianceRepository extends RegulatoryComplianceRepository {
    constructor(private graphqlClient: any) {
        super();
    }

    async getThreshold(
        tenantId: string,
        query: ThresholdQuery
    ): Promise<ThresholdRecord | null> {
        // MANDATORY: Safety validation for repository access
        await this.validateRepositorySafety({
            operation: 'GET_THRESHOLD',
            domain: 'REGULATORY',
            tenantId,
            entityType: 'ThresholdRecord',
            criticalityLevel: 'HIGH'
        });

        const GET_THRESHOLD_QUERY = `
            query GetRegulatoryThreshold($tenantId: String!, $frameworkId: String!, $thresholdType: String!) {
                getRegulatoryThreshold(tenantId: $tenantId, frameworkId: $frameworkId, thresholdType: $thresholdType) {
                    tenant
                    framework_id
                    threshold_type
                    value
                    unit
                    lastUpdated
                    updatedBy
                }
            }
        `;

        try {
            const result = await this.graphqlClient.query({
                query: GET_THRESHOLD_QUERY,
                variables: {
                    tenantId,
                    frameworkId: query.framework_id,
                    thresholdType: query.threshold_type
                }
            });

            return result.data?.getRegulatoryThreshold || null;
        } catch (error) {
            console.error('Failed to get regulatory threshold:', error);
            return null;
        }
    }

    async getThresholdsBatch(
        tenantId: string,
        queries: ThresholdQuery[]
    ): Promise<ThresholdRecord[]> {
        // MANDATORY: Safety validation for repository access
        await this.validateRepositorySafety({
            operation: 'GET_THRESHOLDS_BATCH',
            domain: 'REGULATORY',
            tenantId,
            entityType: 'ThresholdRecord',
            criticalityLevel: 'HIGH'
        });

        const GET_THRESHOLDS_BATCH_QUERY = `
            query GetRegulatoryThresholdsBatch($tenantId: String!, $queries: [ThresholdQueryInput!]!) {
                getRegulatoryThresholdsBatch(tenantId: $tenantId, queries: $queries) {
                    tenant
                    framework_id
                    threshold_type
                    value
                    unit
                    lastUpdated
                    updatedBy
                }
            }
        `;

        try {
            const result = await this.graphqlClient.query({
                query: GET_THRESHOLDS_BATCH_QUERY,
                variables: {
                    tenantId,
                    queries
                }
            });

            return result.data?.getRegulatoryThresholdsBatch || [];
        } catch (error) {
            console.error('Failed to get regulatory thresholds batch:', error);
            return [];
        }
    }

    async updateThreshold(
        tenantId: string,
        record: ThresholdRecord
    ): Promise<ThresholdRecord> {
        // MANDATORY: Safety validation for repository access
        await this.validateRepositorySafety({
            operation: 'UPDATE_THRESHOLD',
            domain: 'REGULATORY',
            tenantId,
            entityType: 'ThresholdRecord',
            criticalityLevel: 'CRITICAL'
        });

        const UPDATE_THRESHOLD_MUTATION = `
            mutation UpdateRegulatoryThreshold($tenantId: String!, $record: ThresholdRecordInput!) {
                updateRegulatoryThreshold(tenantId: $tenantId, record: $record) {
                    tenant
                    framework_id
                    threshold_type
                    value
                    unit
                    lastUpdated
                    updatedBy
                }
            }
        `;

        const result = await this.graphqlClient.mutate({
            mutation: UPDATE_THRESHOLD_MUTATION,
            variables: {
                tenantId,
                record
            }
        });

        return result.data?.updateRegulatoryThreshold ?? null;
    }

    async emergencyOverrideThreshold(
        tenantId: string,
        record: ThresholdRecord,
        justification: string,
        expectedDurationHours: number,
        approvedBy: string
    ): Promise<ThresholdRecord> {
        // MANDATORY: Safety validation for emergency operations
        await this.validateRepositorySafety({
            operation: 'EMERGENCY_OVERRIDE_THRESHOLD',
            domain: 'REGULATORY',
            tenantId,
            entityType: 'ThresholdRecord',
            criticalityLevel: 'CRITICAL'
        });

        const EMERGENCY_OVERRIDE_MUTATION = `
            mutation EmergencyOverrideThreshold(
                $tenantId: String!
                $record: ThresholdRecordInput!
                $justification: String!
                $expectedDurationHours: Int!
                $approvedBy: String!
            ) {
                emergencyOverrideThreshold(
                    tenantId: $tenantId
                    record: $record
                    justification: $justification
                    expectedDurationHours: $expectedDurationHours
                    approvedBy: $approvedBy
                ) {
                    tenant
                    framework_id
                    threshold_type
                    value
                    unit
                    lastUpdated
                    updatedBy
                }
            }
        `;

        const result = await this.graphqlClient.mutate({
            mutation: EMERGENCY_OVERRIDE_MUTATION,
            variables: {
                tenantId,
                record,
                justification,
                expectedDurationHours,
                approvedBy
            }
        });

        return result.data?.emergencyOverrideThreshold ?? null;
    }
}

/**
 * Mock implementation for development and testing
 * Provides realistic threshold data for all domain types
 */
export class MockRegulatoryComplianceRepository extends RegulatoryComplianceRepository {
    private mockThresholds: Map<string, ThresholdRecord> = new Map();

    constructor() {
        super();
        this.initializeMockData();
    }

    private initializeMockData(): void {
        // System-wide LIFE_CRITICAL thresholds (used as fallback defaults)
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'OVERALL_SAFETY_SCORE', 70);
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'HEALTHCARE_SAFETY_SCORE', 75);
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'FINANCIAL_SAFETY_SCORE', 70);
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'LEGAL_SAFETY_SCORE', 80);
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'OPERATIONAL_SAFETY_SCORE', 65);
        this.setMockThreshold('system', 'LIFE_CRITICAL', 'EMERGENCY_RESPONSE_THRESHOLD', 90);

        // Healthcare thresholds
        this.setMockThreshold('demo-tenant', 'HIPAA', 'OVERALL_SAFETY_SCORE', 75);
        this.setMockThreshold('demo-tenant', 'HIPAA', 'HEALTHCARE_SAFETY_SCORE', 80);
        this.setMockThreshold('demo-tenant', 'HIPAA', 'FINANCIAL_SAFETY_SCORE', 70);
        this.setMockThreshold('demo-tenant', 'HIPAA', 'LEGAL_SAFETY_SCORE', 85);
        this.setMockThreshold('demo-tenant', 'HIPAA', 'OPERATIONAL_SAFETY_SCORE', 70);
        this.setMockThreshold('demo-tenant', 'HIPAA', 'EMERGENCY_RESPONSE_THRESHOLD', 95);

        // Demo-tenant LIFE_CRITICAL thresholds
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'OVERALL_SAFETY_SCORE', 75);
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'HEALTHCARE_SAFETY_SCORE', 80);
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'FINANCIAL_SAFETY_SCORE', 70);
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'LEGAL_SAFETY_SCORE', 85);
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'OPERATIONAL_SAFETY_SCORE', 70);
        this.setMockThreshold('demo-tenant', 'LIFE_CRITICAL', 'EMERGENCY_RESPONSE_THRESHOLD', 95);

        // Manufacturing thresholds (different values)
        this.setMockThreshold('manufacturing-tenant', 'OSHA', 'OVERALL_SAFETY_SCORE', 85);
        this.setMockThreshold('manufacturing-tenant', 'OSHA', 'OPERATIONAL_SAFETY_SCORE', 90);

        // Retail thresholds (different values)
        this.setMockThreshold('retail-tenant', 'PCI_DSS', 'OVERALL_SAFETY_SCORE', 60);
        this.setMockThreshold('retail-tenant', 'PCI_DSS', 'FINANCIAL_SAFETY_SCORE', 80);
    }

    private setMockThreshold(
        tenant: string,
        frameworkId: string,
        thresholdType: string,
        value: number,
        unit = 'percentage'
    ): void {
        const key = `${tenant}:${frameworkId}:${thresholdType}`;
        this.mockThresholds.set(key, {
            tenant,
            framework_id: frameworkId,
            threshold_type: thresholdType,
            value,
            unit,
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
        });
    }

    async getThreshold(
        tenantId: string,
        query: ThresholdQuery
    ): Promise<ThresholdRecord | null> {
        const key = `${tenantId}:${query.framework_id}:${query.threshold_type}`;
        return this.mockThresholds.get(key) || null;
    }

    async getThresholdsBatch(
        tenantId: string,
        queries: ThresholdQuery[]
    ): Promise<ThresholdRecord[]> {
        const results: ThresholdRecord[] = [];
        for (const query of queries) {
            const threshold = await this.getThreshold(tenantId, query);
            if (threshold) {
                results.push(threshold);
            }
        }
        return results;
    }

    async updateThreshold(
        tenantId: string,
        record: ThresholdRecord
    ): Promise<ThresholdRecord> {
        const key = `${tenantId}:${record.framework_id}:${record.threshold_type}`;
        const updatedRecord = {
            ...record,
            tenant: tenantId,
            lastUpdated: new Date().toISOString()
        };
        this.mockThresholds.set(key, updatedRecord);
        return updatedRecord;
    }

    async emergencyOverrideThreshold(
        tenantId: string,
        record: ThresholdRecord,
        justification: string,
        expectedDurationHours: number,
        approvedBy: string
    ): Promise<ThresholdRecord> {
        const key = `${tenantId}:${record.framework_id}:${record.threshold_type}`;
        const overrideRecord = {
            ...record,
            tenant: tenantId,
            lastUpdated: new Date().toISOString(),
            updatedBy: approvedBy
        };
        this.mockThresholds.set(key, overrideRecord);
        
        // Log emergency override for audit purposes
        console.log(`[EMERGENCY_OVERRIDE] ${key} = ${record.value} by ${approvedBy}: ${justification}`);
        
        return overrideRecord;
    }
}
